import type { LabRouteEditorAction } from './actions';
import type { LabRouteEditorState, RouteDraft } from './types';
import { DEFAULT_BID_PRIORITY, draftsEqual } from './types';

// Draft mutations are ignored while a save is in flight; FINISH_SAVE would otherwise overwrite them.
function withDraft(state: LabRouteEditorState, update: (draft: RouteDraft) => RouteDraft): LabRouteEditorState {
    if (!state.draft || state.isSaving) return state;
    const draft = update(state.draft);
    return draft === state.draft ? state : { ...state, draft };
}

export function labRouteEditorReducer(state: LabRouteEditorState, action: LabRouteEditorAction): LabRouteEditorState {
    switch (action.type) {
        case 'SET_SCOPE':
            if (state.firmId === action.firmId && state.routeId === action.routeId) return state;
            return { ...state, firmId: action.firmId, routeId: action.routeId };
        case 'SET_CAN_MANAGE':
            if (state.canManage === action.canManage) return state;
            return { ...state, canManage: action.canManage };
        case 'SYNC_ROUTE': {
            if (state.baseline && draftsEqual(state.baseline, action.baseline)) return state;
            // A clean draft follows the server (another admin's save is adopted); local edits are
            // kept and the user resolves via Save (overwrite) or Discard (adopt).
            const clean = !state.draft || !state.baseline || draftsEqual(state.draft, state.baseline);
            return { ...state, baseline: action.baseline, draft: clean ? action.baseline : state.draft };
        }
        case 'SET_FIELD':
            return withDraft(state, (draft) =>
                draft[action.field] === action.value ? draft : { ...draft, [action.field]: action.value },
            );
        case 'TOGGLE_PROVIDER':
            return withDraft(state, (draft) => ({
                ...draft,
                providers: draft.providers.some(({ providerId }) => providerId === action.providerId)
                    ? draft.providers.filter(({ providerId }) => providerId !== action.providerId)
                    : [...draft.providers, { providerId: action.providerId, bidPriority: DEFAULT_BID_PRIORITY }],
            }));
        case 'SET_BID_PRIORITY':
            return withDraft(state, (draft) => {
                const target = draft.providers.find(({ providerId }) => providerId === action.providerId);
                if (!target || target.bidPriority === action.bidPriority) return draft;
                return {
                    ...draft,
                    providers: draft.providers.map((p) =>
                        p.providerId === action.providerId ? { ...p, bidPriority: action.bidPriority } : p,
                    ),
                };
            });
        case 'TARGET_PROVIDERS':
            return withDraft(state, (draft) => {
                const missing = action.providerIds.filter(
                    (id) => !draft.providers.some(({ providerId }) => providerId === id),
                );
                if (!missing.length) return draft;
                return {
                    ...draft,
                    providers: [
                        ...draft.providers,
                        ...missing.map((providerId) => ({ providerId, bidPriority: DEFAULT_BID_PRIORITY })),
                    ],
                };
            });
        case 'UNTARGET_PROVIDERS':
            return withDraft(state, (draft) => {
                const remaining = draft.providers.filter(({ providerId }) => !action.providerIds.includes(providerId));
                return remaining.length === draft.providers.length ? draft : { ...draft, providers: remaining };
            });
        case 'SET_PROVIDER_SEARCH':
            if (state.providerSearch === action.value) return state;
            return { ...state, providerSearch: action.value };
        case 'DISCARD':
            if (!state.baseline || state.draft === state.baseline || state.isSaving) return state;
            return { ...state, draft: state.baseline };
        case 'START_SAVE':
            if (state.isSaving) return state;
            return { ...state, isSaving: true };
        case 'FINISH_SAVE':
            return { ...state, isSaving: false, baseline: action.saved, draft: action.saved };
        case 'FAIL_SAVE':
            if (!state.isSaving) return state;
            return { ...state, isSaving: false };
        default: {
            const _exhaustiveCheck: never = action;
            return state;
        }
    }
}
